<template>
  <Modal
    content-class="full-height column no-wrap overflow-hidden"
    :modal-style="{ maxWidth: '860px', height: '640px' }"
    persistent
    @close="emit('close')"
  >
    <template #header>
      <div
        class="tw:flex tw:items-center tw:gap-2 tw:text-base tw:font-bold"
        data-testid="header"
      >
        {{ getLocaleText("manage_social_connections.title") }}
      </div>
    </template>

    <div class="tw:flex tw:h-full">
      <div class="tw:w-[246px] tw:flex-shrink-0 tw:px-4 tw:py-2">
        <div class="tw:mb-2 tw:mt-[1px] tw:font-bold">
          {{ getLocaleText("manage_social_connections.integration") }}:
        </div>

        <ManageSocialConnectionsSelectIntegration
          v-model="state.organisationSocialIntegrationId"
          data-testid="manage-social-connections-select-integration"
          platform="InstagramSocialIntegration"
        />

        <SocialConnectionOption
          v-model="state.selectedConnection"
          :accounts="state.accountCountTotal"
          class="tw:mt-4"
          data-testid="social-connection-option"
          :hashtags="state.hashtagTotalCollection.size"
        />
      </div>

      <ManageSocialConnectionsAccounts
        v-if="state.selectedConnection === 'accounts'"
        data-testid="manage-social-connections-accounts"
        :has-accounts="state.accountCountTotal > 0"
        :instagram-handle-social-connections="
          state.instagramHandleSocialConnections
        "
        :is-enabled="!!state.organisationSocialIntegrationId"
        :is-loading="state.loading"
        :pagination="state.pagination"
        :selected-ids="state.selectedIntegrationIds"
        @add-account="handleAddSocialConnection"
        @delete="handleAccountDelete"
        @pagination-changed="handlePaginationChanged"
        @search-account="handleSearch"
        @toggle-item-selection="handleSelectAccount"
        @toggle-select-all="handleSelectAllAccounts"
      />
      <ManageSocialConnectionsHashtags
        v-else-if="state.selectedConnection === 'hashtags'"
        data-testid="manage-social-connections-hashtags"
        @add-hashtag="handleAddHashtag"
        @add-hashtag-error="handleAddHashtagError"
        @delete="handleHashtagDelete"
        @pagination-changed="handlePaginationChanged"
        @search-hashtag="handleSearch"
        @toggle-item-selection="handleSelectHashtag"
        @toggle-select-all="handleSelectAllHashtags"
      />
    </div>

    <template #buttons>
      <div class="tw:flex tw:w-full tw:gap-2">
        <Transition name="fade">
          <AlertMessage
            v-if="Boolean(alert.message)"
            class="tw:p-0! tw:px-1!"
            :type="alert.type"
            >{{ alert.message }}
          </AlertMessage>
        </Transition>
        <BaseButton
          class="tw:ml-auto"
          data-testid="cancel-button"
          :label="getLocaleText('global.cancel')"
          new-design
          @click="emit('close')"
        />
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { computed, provide, reactive, toRef, watch } from "vue";

import { $streemApiV1 } from "shared/boot/api";
import { getLocalePluralText, getLocaleText } from "shared/boot/i18n";
import { AlertMessage, BaseButton } from "shared/components/base";
import Modal from "shared/components/core/modals/Modal.vue";
import SocialConnectionOption from "shared/components/integrations/SocialConnectionOption.vue";
import { type PaginationData } from "shared/components/Pagination";
import { useSelectionState } from "shared/composables/useSelectionState";
import { useTransientAlert } from "shared/composables/useTransientAlert";
import { INSTAGRAM_MONITOR_COOLING_PERIOD_DAYS } from "shared/constants/social";
import { orderBy } from "shared/helpers/array";
import { withinAMinute } from "shared/helpers/date";
import ModalService from "shared/modals/ModalService";
import { InstagramHandlesOrganisationSocialIntegration } from "shared/resources";
import InstagramHashtagsOrganisationSocialIntegration from "shared/resources/InstagramHashtagsOrganisationSocialIntegration";
import { type Nullable } from "shared/types";

import ManageSocialConnectionsAccounts from "./ManageSocialConnectionsAccounts.vue";
import ManageSocialConnectionsHashtags from "./ManageSocialConnectionsHashtags.vue";
import ManageSocialConnectionsSelectIntegration from "./ManageSocialConnectionsSelectIntegration.vue";
import {
  type ConnectionId,
  type HashtagId,
  type HashtagWithId,
  type IntegrationWithId,
  type ManageSocialConnectionsState,
  getAccountError,
  getHashtagError,
} from "./types";
import { HASHTAG_CONTEXT, useHashtagContext } from "./useHashtagContext";

function defaultState(
  props: Partial<ManageSocialConnectionsState> = {}
): ManageSocialConnectionsState {
  return {
    account: "",
    accountCount: 0,
    accountCountTotal: 0,
    hashtag: "",
    hashtagCollection: new Map<HashtagId, HashtagWithId>(),
    hashtagTotalCollection: new Map<HashtagId, HashtagWithId>(),
    instagramHandleSocialConnections: new Map<
      ConnectionId,
      IntegrationWithId
    >(),
    loading: false,
    organisationSocialIntegrationId: "",
    selectedHashtagIds: new Set<HashtagId>(),
    selectedIntegrationIds: new Set<ConnectionId>(),
    pagination: {
      page: 1,
      pageCount: 0,
      pageSize: 20,
    },
    searchQuery: "",
    selectedConnection: "accounts",
    ...props,
  };
}

const state = reactive<ManageSocialConnectionsState>(defaultState());

const { alert, showSuccess, showWarning } = useTransientAlert();

const hashtagContext = useHashtagContext({
  organisationSocialIntegrationId: toRef(
    state,
    "organisationSocialIntegrationId"
  ),
  hasHashtags: computed(() => state.hashtagTotalCollection.size > 0),
  hashtagCollection: toRef(state, "hashtagCollection"),
  hashtagPagination: toRef(state, "pagination"),
  hashtagSelectedIds: toRef(state, "selectedHashtagIds"),
  hashtagTotalCollection: toRef(state, "hashtagTotalCollection"),
  hashtagIsLoading: toRef(state, "loading"),
  hashtagIsCurrentView: computed(() => state.selectedConnection === "hashtags"),
});

provide(HASHTAG_CONTEXT, hashtagContext);

const {
  toggleSelectAll: handleSelectAllAccounts,
  toggleItemSelection: handleSelectAccount,
} = useSelectionState({
  selectedIds: toRef(state, "selectedIntegrationIds"),
  allItems: toRef(state, "instagramHandleSocialConnections"),
  pagination: toRef(state, "pagination"),
});

const {
  toggleSelectAll: handleSelectAllHashtags,
  toggleItemSelection: handleSelectHashtag,
} = useSelectionState({
  selectedIds: toRef(state, "selectedHashtagIds"),

  allItems: hashtagContext.filteredHashtagCollection,
  pagination: toRef(state, "pagination"),
});

const instagramCoolingOffPeriod = computed<Date>(() => {
  const now = new Date();
  now.setDate(now.getDate() - INSTAGRAM_MONITOR_COOLING_PERIOD_DAYS);

  return now;
});

async function loadSocialConnections(): Promise<void> {
  if (!state.organisationSocialIntegrationId) return;

  try {
    state.loading = true;

    const response = await InstagramHandlesOrganisationSocialIntegration.where({
      organisation_social_integration_id: state.organisationSocialIntegrationId,
      ...(state.searchQuery && { username: { match: state.searchQuery } }),
    })
      .includes(["instagramHandle"])
      .order({ username: "asc" })
      .page(state.pagination.page)
      .per(state.pagination.pageSize)
      .stats({ total: "count" })
      .all();

    const data = orderBy(response.data, {
      getPath: "updatedAt",
      sortFn: (updatedAt: string) => withinAMinute(new Date(updatedAt)),
      direction: "desc",
    });

    state.instagramHandleSocialConnections = new Map(
      data.map((connection) => [connection.id, connection])
    );

    state.accountCount = response.meta.stats.total.count;

    state.accountCountTotal = Math.max(
      state.accountCountTotal,
      response.meta.stats.total.count
    );

    if (state.selectedConnection === "accounts") {
      state.pagination.pageCount =
        Math.ceil(state.accountCount / state.pagination.pageSize) || 0;
    }
  } catch {
    showWarning(getLocaleText("manage_social_connections.error_fetching"));
  } finally {
    state.loading = false;
  }
}

async function loadHashtags(): Promise<void> {
  if (!state.organisationSocialIntegrationId) return;

  const id = state.organisationSocialIntegrationId;

  try {
    state.loading = true;

    const response = await InstagramHashtagsOrganisationSocialIntegration.where(
      {
        organisation_social_integration_id: id,
        ...(state.searchQuery && { hashtag: { match: state.searchQuery } }),
        not_archived_or_archived_after:
          instagramCoolingOffPeriod.value.toISOString(),
      }
    )
      .order({ hashtag: "asc" })
      .stats({ total: "count" })
      .per(999)
      .all();

    const data = orderBy(response.data, {
      getPath: "archivedAt",
      sortFn: (archivedAt: string) => archivedAt !== null,
      direction: "asc",
    });

    state.hashtagCollection = new Map(data.map((tag) => [tag.id, tag]));

    // Keep the larger collection - either existing total or current data
    state.hashtagTotalCollection =
      state.hashtagTotalCollection.size > data.length
        ? state.hashtagTotalCollection
        : new Map(data.map((tag) => [tag.id, tag]));

    if (state.selectedConnection === "hashtags") {
      state.pagination.pageSize = 30;

      state.pagination.pageCount =
        Math.ceil(
          state.hashtagTotalCollection.size / state.pagination.pageSize
        ) || 0;
    }
  } catch {
    showWarning(getLocaleText("manage_social_connections.error_fetching"));
  } finally {
    state.loading = false;
  }
}

async function handleAddSocialConnection(accountName: Nullable<string>) {
  if (!accountName) {
    showWarning(getLocaleText("manage_social_connections.account_not_found"));

    return;
  }

  state.account = accountName;
  resetCheckboxState();

  try {
    state.loading = true;

    await $streemApiV1.post<InstagramHandlesOrganisationSocialIntegration>(
      "instagram_handles_organisation_social_integrations",
      {
        params: {
          username: state.account,
          organisation_social_integration_id:
            state.organisationSocialIntegrationId,
        },
      }
    );

    showSuccess(getLocaleText("manage_social_connections.account_added"));
  } catch (error) {
    showWarning(getAccountError(error));
  } finally {
    await loadSocialConnections();
  }
}

async function handleAddHashtag(newHashtag: Nullable<string>) {
  if (!newHashtag) {
    showWarning(
      getLocaleText("manage_social_connections.hashtag_added_validation")
    );

    return;
  }

  state.hashtag = newHashtag;

  resetCheckboxState();

  try {
    state.loading = true;

    await $streemApiV1.post<InstagramHandlesOrganisationSocialIntegration>(
      "instagram_hashtags_organisation_social_integrations",
      {
        params: {
          hashtag: state.hashtag,
          organisation_social_integration_id:
            state.organisationSocialIntegrationId,
        },
      }
    );

    showSuccess(getLocaleText("manage_social_connections.hashtag_added"));
  } catch (error) {
    showWarning(getHashtagError(error));
  } finally {
    resetPagination();
    state.searchQuery = "";
    await loadHashtags();
  }
}

function handleAddHashtagError(message: string): void {
  showWarning(message);
}

function handleSearch(searchTerm: string): void {
  state.searchQuery = searchTerm;
  resetCheckboxState();
  resetPagination();
  refreshData();
}

function handlePaginationChanged(updatedPagination: PaginationData): void {
  state.pagination = updatedPagination;
  refreshData();
}

function resetCheckboxState(): void {
  state.instagramHandleSocialConnections = new Map();
  state.hashtagCollection = new Map();

  state.selectedIntegrationIds = new Set();
  state.selectedHashtagIds = new Set();
}

function resetPagination(): void {
  if (state.selectedConnection === "accounts") {
    state.pagination = {
      page: 1,
      pageSize: 20,
      pageCount: 0,
    };
  }
}

async function removeConnections(ids: ConnectionId[]): Promise<void> {
  try {
    await $streemApiV1.post(
      "instagram_handles_organisation_social_integrations/bulk_archive",
      { params: { ids } }
    );

    showSuccess(
      getLocalePluralText(
        "manage_social_connections.account_removed",
        ids.length
      )
    );

    state.accountCount = 0;
    state.accountCountTotal = 0;
  } catch {
    showWarning(
      getLocalePluralText(
        "manage_social_connections.account_remove_error",
        ids.length
      )
    );
  }
}

async function removeHashtags(ids: HashtagId[]): Promise<void> {
  try {
    await $streemApiV1.post(
      "instagram_hashtags_organisation_social_integrations/bulk_archive",
      { params: { ids } }
    );

    showSuccess(
      getLocalePluralText(
        "manage_social_connections.hashtag_removed",
        ids.length
      )
    );
  } catch {
    showWarning(
      getLocalePluralText(
        "manage_social_connections.hashtag_remove_error",
        ids.length
      )
    );
  }
}

function handleAccountDelete(id?: ConnectionId): void {
  const selectedIds = state.selectedIntegrationIds;

  if (id) {
    state.selectedIntegrationIds = new Set();
    handleSelectAccount(id, true);
  }

  ModalService.open("ConfirmDialogModal", {
    props: {
      type: "confirm",
      theme: "danger",
      title: getLocaleText("modals.confirm_modal.title"),
      dialogMaxWidth: "350px",
      message: getLocalePluralText(
        "manage_social_connections.account_remove",
        state.selectedIntegrationIds.size
      ),
      confirmLabel: getLocaleText("global.confirm"),
    },
    events: {
      close: () => {
        state.selectedIntegrationIds = selectedIds;
        ModalService.close("ConfirmDialogModal");
      },
      confirm: async () => {
        const idsToDelete = id
          ? [id]
          : Array.from(state.selectedIntegrationIds);

        await removeConnections(idsToDelete);
        resetCheckboxState();
        resetPagination();
        loadSocialConnections();
      },
    },
  });
}

function handleHashtagDelete(id?: HashtagId): void {
  const selectedIds = state.selectedHashtagIds;

  if (id) {
    state.selectedHashtagIds = new Set();
    handleSelectHashtag(id, true);
  }

  ModalService.open("ConfirmDialogModal", {
    props: {
      type: "confirm",
      theme: "danger",
      title: getLocaleText("modals.confirm_modal.title"),
      dialogMaxWidth: "350px",
      message: getLocalePluralText(
        "manage_social_connections.hashtag_remove",
        state.selectedHashtagIds.size
      ),
      confirmLabel: getLocaleText("global.confirm"),
    },
    events: {
      close: () => {
        state.selectedHashtagIds = selectedIds;
        ModalService.close("ConfirmDialogModal");
      },
      confirm: async () => {
        const idsToDelete = id ? [id] : Array.from(state.selectedHashtagIds);

        await removeHashtags(idsToDelete);
        resetPagination();
        resetCheckboxState();
        state.searchQuery = "";
        loadHashtags();
      },
    },
  });
}

async function handleAccountChange(): Promise<void> {
  Object.assign(
    state,
    defaultState({
      organisationSocialIntegrationId: state.organisationSocialIntegrationId,
      selectedConnection: state.selectedConnection,
    })
  );

  await Promise.all([loadSocialConnections(), loadHashtags()]);
}

function refreshData(): Promise<void> {
  if (state.selectedConnection === "accounts") {
    return loadSocialConnections();
  }

  return loadHashtags();
}

watch(
  () => state.selectedConnection,
  async () => {
    resetPagination();
    state.searchQuery = "";
    await refreshData();
  }
);

watch(() => state.organisationSocialIntegrationId, handleAccountChange, {
  immediate: true,
});

const emit = defineEmits<{ close: []; "upload-csv": [] }>();

defineExpose({
  alert,
  handleAccountDelete,
  handleAddHashtag,
  loadHashtags,
  loadSocialConnections,
  state,
});
</script>

<style lang="scss" scoped>
@import "shared/css/utils/transitions/fade";
</style>
